import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { getEvents } from '../services/api';
import { getErrorMessage } from '../utils/errors';
import { useAuth } from '../contexts/AuthContext';
import { features } from '../config/features';
import type { RacingEvent } from '../types';
import { PageHeader, Alert, Badge, Button, DataTable } from '@racing/ui';

type StatusVariant = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'solid';

const statusVariants: Record<string, StatusVariant> = {
  DRAFT: 'neutral',
  PUBLISHED: 'info',
  REGISTRATION_OPEN: 'success',
  REGISTRATION_CLOSED: 'warning',
  COMPLETED: 'solid',
  CANCELLED: 'danger',
};

const formatDate = (value: unknown) =>
  value ? new Date(value as string).toLocaleDateString() : '—';

const EventListPage = () => {
  const [events, setEvents] = useState<RacingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { isAdmin, isTrackOperator } = useAuth();

  const canCreateEvents = isAdmin || isTrackOperator;

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getEvents();
      setEvents(data);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  /**
   * Name, date and status identify an event, so they stay at every width. The
   * rest earn their place as the viewport grows — see lib/column-priority.
   */
  const columns = useMemo<ColumnDef<RacingEvent, any>[]>(
    () => [
      { accessorKey: 'event_name', header: 'Event Name', meta: { width: 200 } },
      {
        accessorKey: 'start_date',
        header: 'Start Date',
        cell: ({ getValue }) => formatDate(getValue()),
        meta: { width: 130 },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => {
          const value = getValue<string | null>();
          return (
            <Badge variant={value ? (statusVariants[value] ?? 'neutral') : 'brand'}>
              {value ? value.replace(/_/g, ' ') : 'TIMING ONLY'}
            </Badge>
          );
        },
        meta: { width: 170 },
      },
      { accessorKey: 'track_name', header: 'Track', meta: { width: 160, priority: 'sm' } },
      { accessorKey: 'year', header: 'Year', meta: { width: 90, numeric: true, priority: 'lg' } },
      {
        accessorKey: 'end_date',
        header: 'End Date',
        cell: ({ getValue }) => formatDate(getValue()),
        meta: { width: 130, priority: 'lg' },
      },
      {
        id: 'body_name',
        header: 'Sanctioning Body',
        accessorFn: (row) => row.body_abbreviation || row.body_name || '—',
        meta: { width: 160, priority: 'lg' },
      },
      ...(features.eventRegistrations
        ? [
            {
              accessorKey: 'registration_count',
              header: 'Registrations',
              meta: { width: 130, numeric: true, priority: 'lg' },
            } as ColumnDef<RacingEvent, any>,
          ]
        : []),
    ],
    []
  );

  return (
    <div className="flex min-w-0 flex-col">
      <PageHeader
        title="Events"
        subtitle={
          loading
            ? 'Loading events…'
            : `${events.length} event${events.length === 1 ? '' : 's'} in your scope`
        }
        actions={
          canCreateEvents && (
            <Button className="w-full sm:w-auto" onClick={() => navigate('/events/new')}>
              <Plus /> New Event
            </Button>
          )
        }
      />

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <DataTable
        data={events}
        columns={columns}
        getRowId={(row) => String(row.event_id)}
        loading={loading}
        onRowClick={(row) => navigate(`/events/${row.event_id}`)}
        emptyMessage="No events in your scope yet"
        pageSizeOptions={[10, 25, 50]}
        className="h-[calc(100dvh-15rem)] min-h-[22rem]"
      />
    </div>
  );
};

export default EventListPage;
